import { useState, useRef, useEffect } from "react";
import { Download, Database, Shield, CheckCircle, AlertCircle, Loader2, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 5000;

export default function ExportarDB() {
  const [estado, setEstado] = useState("idle"); // idle | generando | listo | error
  const [progreso, setProgreso] = useState("");
  const [completado, setCompletado] = useState(false);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  const urlEstado = `${API}/api/backups/dump-directo/estado`;
  const urlIniciar = `${API}/api/backups/dump-directo/iniciar`;
  const urlDescarga = `${API}/api/backups/dump-directo/descargar`;

  const detenerPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  useEffect(() => () => detenerPolling(), []);

  const descargarArchivoListo = async () => {
    try {
      const res = await axios.get(urlDescarga, { responseType: "blob" });

      const disposition = res.headers["content-disposition"] || "";
      const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      const filename = match ? match[1].replace(/['"]/g, "") : `edmundo_backup_${new Date().toISOString().slice(0, 10)}.zip`;

      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setCompletado(true);
      setEstado("idle");
      toast.success("Base de datos descargada exitosamente");
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      setError(msg);
      setEstado("error");
      toast.error("Error al descargar: " + msg);
    }
  };

  const consultarEstado = async () => {
    try {
      const res = await axios.get(urlEstado);
      const data = res.data;
      setProgreso(data.progreso || "");

      if (data.estado === "listo") {
        detenerPolling();
        setEstado("listo");
        await descargarArchivoListo();
      } else if (data.estado === "error") {
        detenerPolling();
        setEstado("error");
        setError(data.error || "Error generando el backup");
        toast.error("Error generando el backup: " + (data.error || ""));
      }
    } catch (err) {
      // Hiccup de red durante el polling: se reintenta en el próximo tick
    }
  };

  const handleIniciar = async () => {
    setError(null);
    setCompletado(false);
    setEstado("generando");
    setProgreso("Iniciando...");

    try {
      await axios.post(urlIniciar);

      detenerPolling();
      pollRef.current = setInterval(consultarEstado, POLL_INTERVAL_MS);
      consultarEstado();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      setError(msg);
      setEstado("error");
      toast.error("Error al iniciar: " + msg);
    }
  };

  const generando = estado === "generando";

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-blue-900/40">
            <Database size={40} className="text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">EDMUNDO ERP</h1>
          <p className="text-gray-400 text-base">Exportación completa de base de datos</p>
        </div>

        {/* Card principal */}
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8 shadow-2xl">

          {/* Info */}
          <div className="mb-6 space-y-3">
            <div className="flex items-start gap-3 bg-gray-800 rounded-xl p-4">
              <Shield size={18} className="text-blue-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-white text-sm font-medium">Base de datos completa (MongoDB BSON)</p>
                <p className="text-gray-400 text-xs mt-0.5">Todas las colecciones en formato nativo. Restaurable con <code className="text-green-400">mongorestore</code>.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 bg-gray-800 rounded-xl p-4">
              <Database size={18} className="text-yellow-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-white text-sm font-medium">Generación en segundo plano</p>
                <p className="text-gray-400 text-xs mt-0.5">El backup se genera en background (no bloquea el servidor). Esta página consulta el progreso automáticamente cada 5 segundos.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 bg-gray-800 rounded-xl p-4">
              <Download size={18} className="text-green-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-white text-sm font-medium">Imágenes y archivos subidos</p>
                <p className="text-gray-400 text-xs mt-0.5">Comprobantes de Caja Chica, recibos de Banco ECO, recibos de deuda — todo incluido.</p>
              </div>
            </div>
          </div>

          {/* Estado */}
          {completado && (
            <div data-testid="dump-completado-banner" className="flex items-center gap-3 bg-green-900/30 border border-green-700 rounded-xl p-4 mb-5">
              <CheckCircle size={20} className="text-green-400 flex-shrink-0" />
              <p className="text-green-300 text-sm font-medium">Descarga completada exitosamente</p>
            </div>
          )}

          {generando && progreso && (
            <div data-testid="dump-progreso-banner" className="flex items-center gap-3 bg-blue-900/30 border border-blue-700 rounded-xl p-4 mb-5">
              <Loader2 size={18} className="text-blue-400 flex-shrink-0 animate-spin" />
              <p data-testid="dump-progreso-texto" className="text-blue-300 text-sm font-medium">{progreso}</p>
            </div>
          )}

          {error && (
            <div data-testid="dump-error-banner" className="flex items-start gap-3 bg-red-900/30 border border-red-700 rounded-xl p-4 mb-5">
              <AlertCircle size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-red-300 text-sm font-medium">Error en la generación/descarga</p>
                <p data-testid="dump-error-texto" className="text-red-400 text-xs mt-1">{error}</p>
              </div>
            </div>
          )}

          {/* Botón principal */}
          <button
            onClick={handleIniciar}
            disabled={generando}
            data-testid="iniciar-dump-btn"
            className="w-full flex items-center justify-center gap-3 py-4 px-6 bg-blue-600 hover:bg-blue-500 disabled:bg-blue-800 disabled:opacity-60 text-white font-semibold text-lg rounded-xl transition-all duration-200 shadow-lg shadow-blue-900/40 hover:shadow-blue-700/40 hover:scale-[1.02] active:scale-[0.98]"
          >
            {generando ? (
              <>
                <Loader2 size={22} className="animate-spin" />
                <span>Generando en segundo plano...</span>
              </>
            ) : (
              <>
                <PlayCircle size={22} />
                <span>Iniciar Generación (.zip)</span>
              </>
            )}
          </button>

        </div>

        {/* Footer */}
        <p className="text-center text-gray-700 text-xs mt-6">
          Solo superadmin · La descarga se hace con tu sesión, no con un enlace público
        </p>
        <a href="/reset-sistema" data-testid="link-reset-sistema" className="block text-center text-red-500/70 hover:text-red-400 text-xs mt-3 underline">
          Reset del sistema a cero (zona de peligro)
        </a>
      </div>
    </div>
  );
}
